import AssessmentIcon from '@material-ui/icons/Assessment';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import BoardIcon from '@material-ui/icons/DeveloperBoard';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import MemoryIcon from '@material-ui/icons/Memory';
import PageviewIcon from '@material-ui/icons/Pageview';
import PublishIcon from '@material-ui/icons/Publish';
import WavesIcon from '@material-ui/icons/Waves';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import OntologyProjectAssociations from '../../application-core/associated-project/OntologyProjectAssociations';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import PageLayout from '../../core/PageLayout';
import PrivateRoute from '../../core/PrivateRoute';
import Unsaved from '../../core/Unsaved';
import DataManagerIcon from '../../core/icons/DataManager';
import ProjectContainer from '../../core/project/ProjectContainer';
import ProjectData from '../../core/project/ProjectData';
import ProjectExport from '../../core/project/ProjectExport';
import ProjectPermissions from '../../core/project/ProjectPermissions';
import ProjectSummary from '../../core/project/ProjectSummary';
import { BASE_OPTIONS } from '../../core/project/constants';
import { UserConsumer } from '../../core/providers/UserProvider';
import ReportManager from '../../core/report/ReportManager';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import VersionManager from '../../core/version/VersionManager';
import TaskManager from '../../core/workflow/TaskManager';
import GS from '../../grafsync/grafsync';
import AdvancedSearch from '../../vocabulary/advanced-search/AdvancedSearch';
import ResourceResultDisplay from '../../vocabulary/advanced-search/ResourceResultDisplay';
import DataImportContainer from '../DataImportContainer';
import DatasetManager from '../DatasetManager';
import FloatingDataManager from '../floating/FloatingDataManager';
import DataProjectDetail from './DataProjectDetail';
import DataProjectInfoContainer from './DataProjectInfoContainer';

const options = [
  {
    name: "Data Manager Home",
    url: '',
    icon: <DataManagerIcon />
  },
  ...BASE_OPTIONS,
  ...[
    {
      name: "Template Manager",
      url: 'project/templatemanager',
      icon: <BoardIcon />,
    },
    {
      name: "Import",
      url: 'project/import',
      entitlement: APPENTITLEMENTS.CAN_IMPORT,
      icon: <PublishIcon />,
      maxComponent: 1
    },
    {
      name: "Export Project",
      url: 'project/export',
      entitlement: APPENTITLEMENTS.CAN_EXPORT_PROJECT,
      icon: <GetAppIcon />
    },
    {
      name: "Free-floating Items",
      url: 'project/floating',
      icon: <WavesIcon />
    },
    {
      name: "Advanced Search",
      url: 'project/search',
      icon: <PageviewIcon />
    },
    {
      name: "Editor",
      url: 'project/editor',
      icon: <EditIcon />
    },
    {
      name: "Reports",
      url: 'project/report',
      icon: <AssessmentIcon />
    },
    {
      name: "Jobs Manager",
      url: 'project/jobsmanager',
      icon: <AssignmentIndIcon />
    },
    {
      name: "Versions",
      url: 'project/version',
      icon: <MemoryIcon />
    }
  ]];

const viewOptions = [
  {
    value: "http://www.w3.org/2000/01/rdf-schema#label",
    label: "Label",
    visible: true,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    label: "Class",
    visible: true,
    format: "class",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "iri",
    label: "IRI",
    visible: true
  }
];

class DataProject extends Component {

  render() {

    const { id, applicationURL, applicationTitle, graph, loadKey, lockedGraphs } = this.props;
    const baseLink = APPLICATION_PATHS.DATA_MANAGER + '/project/detail';
    const refreshKey = id + loadKey;

    const pageCrumb = <Switch>
      <Route path={applicationURL + "/project/detail"} render={() => <span>Project Home</span>} />
      <Route path={applicationURL + "/project/import"} render={() => <span>Import</span>} />
      <Route path={applicationURL + "/project/export"} render={() => <span>Export Project</span>} />
      <Route path={applicationURL + "/project/edit"} render={() => <span>Project Information</span>} />
      <Route path={applicationURL + "/project/permissions"} render={() => <span>Permissions</span>} />
      <Route path={applicationURL + "/project/templatemanager"} render={() => <span>Template Manager</span>} />
      <Route path={applicationURL + "/project/floating"} render={() => <span>Free-floating Items</span>} />
      <Route path={applicationURL + "/project/search"} render={() => <span>Advanced Search</span>} />
      <Route path={applicationURL + "/project/editor"} render={() => <span>Editor</span>} />
      <Route path={applicationURL + "/project/report"} render={() => <span>Reports</span>} />
      <Route path={applicationURL + "/project/version"} render={() => <span>Versions</span>} />
      <Route path={applicationURL + "/project/jobsmanager"} render={() => <span>Jobs Manager</span>} />
      <Route render={() => <span>Edit</span>} />
    </Switch>;

    return (
      <UserConsumer>
        {({ user, entitlements }) => {
          // Must have this entitlement to do any of this
          if (!entitlements.includes(ENTITLEMENTS.VIEW_DATA_PROJECT)) {
            return <Redirect to={{ pathname: '/' }} />;
          }

          return <PageLayout
            title={applicationTitle}
            baseURL={applicationURL}
            graph={graph}
            id={id}
            options={options}
            includeJumps={true}
            optionParams={"#project=" + id}
            layoutTitle={ProjectSummary}
            layoutContainer={ProjectContainer}
            breadcrumb={{
              baseURL: applicationURL,
              title: applicationTitle,
              static: pageCrumb,
              path: ["project", "dataset", "floating", "search", "editor", "report", "version", "jobsmanager"],
              url: {
                project: "detail",
                dataset: "dataset",
                floating: "floating",
                search: "search",
                editor: "editor",
                report: "report",
                version: "version",
                jobsmanager: "jobsmanager"
              }
            }}>
            <Unsaved />
            <Switch>
              <Route path={applicationURL + "/project/detail"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props}
                  passthroughComponent={DataProjectDetail}
                  key={refreshKey}
                  id={id}
                  user={user}
                  projectType="DataProject"
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/edit"} render={(props) =>
                <ProjectData {...props} {...this.props}
                  id={id}
                  key={refreshKey}
                  projectType="DataProject"
                  entitlement={ENTITLEMENTS.EDIT_DATA_PROJECT}
                  graph={graph}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/permissions"}
                entitlement={
                  [
                    APPENTITLEMENTS.EDIT_PERMISSIONS
                  ]
                }
                component={DataProjectInfoContainer}
                passthroughComponent={ProjectPermissions}
                id={id}
                lockedGraphs={lockedGraphs}
                projectType="DataProject"
                user={user}
                graph={graph}
                project={id} />
              <Route path={applicationURL + "/project/templatemanager"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={OntologyProjectAssociations}
                  key={refreshKey}
                  baseLink={baseLink}
                  projectType="DataProject"
                  id={id}
                  graph={graph}
                  user={user}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/import"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_IMPORT
                  ]
                }
                component={DataProjectInfoContainer}
                loadKey={loadKey}
                passthroughComponent={GS.outstandingEdits() > 0 ? Unsaved : DataImportContainer}
                user={user}
                projectType="DataProject"
                project={id}
                baseLink={baseLink}
                lockedGraphs={lockedGraphs}
                graph={graph}
                id={id} />
              <PrivateRoute path={applicationURL + "/project/export"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_EXPORT_PROJECT
                  ]
                }
                component={ProjectExport}
                exportName="dataprojectexport"
                projectType="DataProject"
                graph={graph}
                id={id} />
              <Route path={applicationURL + "/project/dataset"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props}
                  passthroughComponent={DatasetManager}
                  key={refreshKey}
                  id={id}
                  projectType="DataProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/floating"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={FloatingDataManager}
                  key={refreshKey}
                  id={id}
                  projectType="DataProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/search"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={AdvancedSearch}
                  key={refreshKey}
                  id={id}
                  optimiseForLargeData={false}
                  editMode={false}
                  viewOptions={viewOptions}
                  componentType="Dataset"
                  label="label"
                  baseLink={APPLICATION_PATHS.DATA_MANAGER}
                  rowDisplay={ResourceResultDisplay}
                  resourceIn="https://grafsync.graphifi.com/schema/gs/inDataset"
                  labelIRI="http://www.w3.org/2000/01/rdf-schema#label"
                  searchClass="https://grafsync.graphifi.com/schema/gs/Resource"
                  projectType="DataProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/editor"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={AdvancedSearch}
                  key={refreshKey}
                  id={id}
                  editMode={true}
                  optimiseForLargeData={false}
                  editRequirements={ENTITLEMENTS.EDIT_DATASET}
                  viewOptions={viewOptions}
                  componentType="Dataset"
                  label="label"
                  baseLink={APPLICATION_PATHS.DATA_MANAGER}
                  rowDisplay={ResourceResultDisplay}
                  resourceIn="https://grafsync.graphifi.com/schema/gs/inDataset"
                  labelIRI="http://www.w3.org/2000/01/rdf-schema#label"
                  searchClass="https://grafsync.graphifi.com/schema/gs/Resource"
                  projectType="DataProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/report"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={ReportManager}
                  key={refreshKey}
                  id={id}
                  user={user}
                  projectType="DataProject"
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/version"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={VersionManager}
                  key={refreshKey}
                  id={id}
                  projectType="DataProject"
                  exportName="dataprojectexport"
                  user={user}
                  graph={graph}
                  historyOption={{
                    label: "All Resources In Project",
                    resource: true,
                    path: "https://grafsync.graphifi.com/schema/gs/inDataset"
                  }}
                  project={id} />} />
              <Route path={applicationURL + "/project/jobsmanager"} render={(props) =>
                <DataProjectInfoContainer {...props} {...this.props} passthroughComponent={TaskManager}
                  key={refreshKey}
                  id={id}
                  componentType="Dataset"
                  projectType="DataProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
            </Switch>
          </PageLayout>;
        }}
      </UserConsumer>
    );
  }
}

DataProject.propTypes = {
  applicationTitle: PropTypes.string.isRequired,
  applicationURL: PropTypes.string.isRequired,
  lockedGraphs: PropTypes.array,
  loadKey: PropTypes.string,
  id: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired
};

export default DataProject;